import { Builder, BuilderStore } from '@builder.io/react';
import classNames from 'classnames';
import { pipe } from 'fp-ts/lib/function';
import parse from 'html-react-parser';

import { CategoryOverview as CSCategoryOverview } from '@catawiki/cata-style';

import { Category } from '@/shared/models/category';
import { CATEGORIES, CATEGORY_STUB } from '@/shared/stubs/categories';
import { nullableData } from '@/shared/utils/http';

import {
  fetchCategories,
  fetchMenuCategories,
  RemoteCategory,
} from '../api/buyer';
import { formatBuilderDate } from '../formatter';
import { cleanBuilderStringInput, getBuilderString } from '../text';
import { getSpan, TRACER_NAME_BUILDER } from '../utils/tracer';

export enum CategoryLinkType {
  CATEGORY = 'category',
  SEARCH = 'search',
}

type CategoryInput = {
  categoryId: number;
};
interface BuilderInput {
  title: string;
  includedCategories?: CategoryInput[];
  excludedCategories?: CategoryInput[];
  categoryLinkType?: CategoryLinkType;
  endTime?: string;
  endTimes: { time: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: any;
  builderState?: BuilderStore;
}

type Props = BuilderInput & {
  locale: string;
  categories: Category[];
};

const formatEndDateStrings = (
  endTime: string,
  endTimes: { time: string }[]
) => {
  if (endTimes.length > 0) {
    return endTimes
      .map((item) => (item.time && formatBuilderDate(item.time)) || null)
      .filter((item): item is string => !!item);
  }

  if (!endTime) {
    return [
      formatBuilderDate(
        'Mon Sep 14 2020 00:00:00 GMT+0200 (Central European Summer Time)'
      ),
    ];
  }

  return [formatBuilderDate(endTime)];
};

export const searchLinkForCategory = (
  locale: string,
  category: Category,
  biddingEndDates: string[]
) => {
  const url = new URL(category.url);

  url.pathname = `/${locale}/slp/category=${category.id}/`;
  url.search = '';
  url.searchParams.append('reserve_price', '0');
  url.searchParams.append('bidding_end_days', biddingEndDates.join(','));

  return url.toString();
};

const filterCategories = (
  categories: Category[],
  includedCategoryIds: number[],
  excludedCategoryIds: number[]
) =>
  categories
    .filter((category) =>
      includedCategoryIds.length > 0
        ? includedCategoryIds.includes(category.id)
        : true
    )
    .filter((category) =>
      excludedCategoryIds.length > 0
        ? !excludedCategoryIds.includes(category.id)
        : true
    );

const reorderCategories = (
  categories: Category[],
  categoryIdsOrder: number[]
): Category[] => {
  const categoriesById: { [k: number]: Category } = categories.reduce(
    (acc, category) => ({ ...acc, [category.id]: category }),
    {}
  );

  return categoryIdsOrder.map((id) => categoriesById[id]).filter(Boolean);
};

export const CategoryOverview = ({
  title,
  categories = CATEGORIES,
  attributes,
  includedCategories = [],
  excludedCategories = [],
  // End Time is deprecated and will remove after endTimes releases.
  endTime = 'Mon Sep 14 2020 00:00:00 GMT+0200 (Central European Summer Time)',
  endTimes = [],
  categoryLinkType,
  builderState,
  locale,
}: Props) => {
  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale;
  const localizedTitle = getBuilderString(title, blockLocale);

  const includedCategoryIds = includedCategories
    .map((c) => c.categoryId)
    .filter((item) => item);
  const excludedCategoryIds = excludedCategories
    .map((c) => c.categoryId)
    .filter((item) => item);

  const filteredCategories = filterCategories(
    categories,
    includedCategoryIds,
    excludedCategoryIds
  );

  const orderedCategories =
    includedCategoryIds.length > 0
      ? reorderCategories(filteredCategories, includedCategoryIds)
      : filteredCategories;

  const endDateStrings = formatEndDateStrings(endTime, endTimes);

  const categoriesWithCorrectUrl = orderedCategories.map((category) => ({
    ...category,
    url:
      categoryLinkType === CategoryLinkType.SEARCH
        ? searchLinkForCategory(locale, category, endDateStrings)
        : category.url,
  }));

  const categoriesOrStubs =
    categoriesWithCorrectUrl.length > 0
      ? categoriesWithCorrectUrl
      : Array(includedCategoryIds.length).fill(CATEGORY_STUB);

  return (
    <div {...attributes} className={attributes?.className}>
      <div
        className={classNames('u-wrapper', 'p-category-overview', 'tw:mt-l')}
      >
        <div className="u-m-t-xxl-10 p-category-overview-header">
          <div className="u-typography-h2">
            {parse(
              cleanBuilderStringInput(
                localizedTitle ||
                  'View \'no reserve\' auctions <span class="u-typography-h1-serif u-color-brand">by category</span>'
              )
            )}
          </div>
        </div>
        <div className="u-flex u-flex--direction-column u-flex--justify-content-center">
          <CSCategoryOverview categories={categoriesOrStubs} noLazyLoading />
        </div>
      </div>
    </div>
  );
};

const mapRemoteCategoriesToCategories = (categories: RemoteCategory[]) =>
  categories.map((remoteCategory) => {
    const category: Category = {
      id: remoteCategory.id,
      url: remoteCategory.url,
      name: remoteCategory.name,
      imageUrl: remoteCategory.image_url,
      color: remoteCategory.color,
    };
    return category;
  });

const fetchMenuAuctionCategories = async (locale: string) => {
  const eitherCategories = await fetchMenuCategories(locale);
  const categories = pipe(eitherCategories, nullableData) || [];
  return mapRemoteCategoriesToCategories(categories);
};

const fetchAuctionCategories = async (
  categoryIds: number[],
  locale: string
) => {
  const eitherCategories = await fetchCategories(categoryIds, locale);
  const categories = pipe(eitherCategories, nullableData) || [];
  return mapRemoteCategoriesToCategories(categories);
};

export function dataFetcher(locale: string) {
  return async ({ includedCategories = [] }: BuilderInput) => {
    const span = getSpan(TRACER_NAME_BUILDER, 'CategoryOverview.dataFetcher');
    const includedCategoryIds = includedCategories
      .map((c) => c.categoryId)
      .filter((item) => item);

    const categories =
      includedCategoryIds.length > 0
        ? await fetchAuctionCategories(includedCategoryIds, locale)
        : await fetchMenuAuctionCategories(locale);
    span.end();
    return {
      locale,
      categories,
    };
  };
}

export function componentRegister() {
  const name = 'CategoryOverview';
  Builder.registerComponent(CategoryOverview, {
    name,
    inputs: [
      {
        name: 'title',
        friendlyName: 'Title',
        type: 'CatawikiEditor',
      },
      {
        friendlyName: 'Search end time',
        name: 'endTimes',
        type: 'list',
        defaultValue: [],
        subFields: [{ name: 'time', type: 'date', required: true }],
      },
      {
        name: 'includedCategories',
        type: 'list',
        subFields: [{ name: 'categoryId', type: 'number' }],
      },
      {
        name: 'excludedCategories',
        type: 'list',
        subFields: [{ name: 'categoryId', type: 'number' }],
      },
      {
        name: 'categoryLinkType',
        friendlyName: 'Links lead to the categories OR to the search',
        type: 'string',
        defaultValue: CategoryLinkType.CATEGORY,
        enum: Object.values(CategoryLinkType).map((value) => ({
          label: value,
          value,
        })),
      },
    ],
    noWrap: true,
  });
  return name;
}
